import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import { IInstitutionPlan, IModuleAccess, PlanTier } from '../interfaces/institution.interface';

interface CreateInstitutionDto {
    name: string;
    code: string;
    description: string;
    email: string;
}

export const InstitutionService = {
    getInstitutions: async (page: number = 0, size: number = 10) => {
        const response = await axios.get(ENDPOINTS.INSTITUTION.BASE, {
            params: { page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getLoggedInUserInstitutions: async (page: number = 0, size: number = 10) => {
        const response = await axios.get(ENDPOINTS.INSTITUTION.LOGGED_IN_USER_INSTITUTIONS, {
            params: { page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    deleteInstitution: async (institutionId: string) => {
        const response = await axios.delete(
            ENDPOINTS.INSTITUTION.DELETE(institutionId),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    createInstitution: async (institutionData: CreateInstitutionDto) => {
        const response = await axios.post(
            ENDPOINTS.INSTITUTION.BASE,
            institutionData,
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    checkCodeUniqueness: async (code: string): Promise<boolean> => {
        const response = await axios.get(
            ENDPOINTS.INSTITUTION.CHECK_CODE_UNIQUENESS,
            {
                params: { code },
                headers: AuthService.getAuthHeader()
            }
        );
        return response.data;
    },

    /** Approval creates the institution's ONE first administrator from this person (their credentials are e-mailed). */
    approveInstitution: async (institutionId: string, firstAdmin: { firstName: string; lastName: string; email: string }) => {
        const response = await axios.post(
            `${ENDPOINTS.INSTITUTION.BASE}/${institutionId}/approve`,
            firstAdmin,
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    getInstitutionsByUserId: async (userId: string) => {
        const response = await axios.get(
            ENDPOINTS.INSTITUTION.BY_USER_ID(userId),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    toggleInstitutionActive: async (institutionId: string) => {
        const response = await axios.patch(
            ENDPOINTS.INSTITUTION.TOGGLE_ACTIVE(institutionId),
            {},
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    getInstitutionById: async (id: string) => {
        const response = await axios.get(
            `${ENDPOINTS.INSTITUTION.BASE}/${id}`,
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    updateInstitution: async (id: string, dto: object) => {
        const response = await axios.put(
            ENDPOINTS.INSTITUTION.UPDATE(id),
            dto,
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    uploadLogo: async (id: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.put(
            ENDPOINTS.INSTITUTION.UPLOAD_LOGO(id),
            formData,
            {
                headers: {
                    ...AuthService.getAuthHeader(),
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    },

    deleteLogo: async (id: string) => {
        const response = await axios.delete(
            ENDPOINTS.INSTITUTION.DELETE_LOGO(id),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    // Premium dashboard entitlement.
    // Read: any institution member (tenancy-guarded server-side). Write: SaaS Operator / SystemAdmin only.
    getPremiumDashboard: async (id: string): Promise<boolean> => {
        const response = await axios.get(
            ENDPOINTS.INSTITUTION.PREMIUM_DASHBOARD(id),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data === true;
    },

    setPremiumDashboard: async (id: string, enabled: boolean) => {
        const response = await axios.patch(
            ENDPOINTS.INSTITUTION.PREMIUM_DASHBOARD(id),
            {},
            { params: { enabled }, headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    // Plan tier (provisioning). Read: member or platform; write: SaaS Operator / SystemAdmin only.
    getPlan: async (id: string): Promise<IInstitutionPlan> => {
        const response = await axios.get(ENDPOINTS.INSTITUTION.PLAN(id), { headers: AuthService.getAuthHeader() });
        return response.data;
    },

    updatePlan: async (id: string, planTier: PlanTier): Promise<IInstitutionPlan> => {
        const response = await axios.put(ENDPOINTS.INSTITUTION.PLAN(id), { planTier }, { headers: AuthService.getAuthHeader() });
        return response.data;
    },

    // Gated dashboard modules. Read: any institution member (tenancy-guarded server-side).
    // Write: SaaS Operator / SystemAdmin only, cross-tenant.
    getEnabledModules: async (id: string): Promise<string[]> => {
        const response = await axios.get(
            ENDPOINTS.INSTITUTION.ENABLED_MODULES(id),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data.modules;
    },

    setEnabledModules: async (id: string, modules: string[]): Promise<void> => {
        await axios.put(
            ENDPOINTS.INSTITUTION.ENABLED_MODULES(id),
            { modules },
            { headers: AuthService.getAuthHeader() }
        );
    },

    // Granted modules and the ones the institution admin switched off. Read: platform or MODULE_MANAGE.
    getModuleAccess: async (id: string): Promise<IModuleAccess> => {
        const response = await axios.get(ENDPOINTS.INSTITUTION.MODULE_ACCESS(id), { headers: AuthService.getAuthHeader() });
        return response.data;
    },

    // Write: institution admin (MODULE_MANAGE). Only granted modules may be switched off.
    setModuleAccess: async (id: string, disabled: string[]): Promise<IModuleAccess> => {
        const response = await axios.put(
            ENDPOINTS.INSTITUTION.MODULE_ACCESS(id),
            { granted: [], disabled },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },
};
